import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useOpenInvoiceCount } from "../api/openInvoices";
import { listImportJobs } from "../api/imports";
import { useSession } from "../auth/useSession";
import type { SessionUser } from "../auth/useSession";
import { DEMO_MARKETING_URL, isDemoMode } from "../lib/env";
import { canOpenHelpGuide, getAvailableHelpGuides, getHelpGuideForPath, HELP_QUERY_PARAM } from "../lib/helpGuides";
import type { HelpGuideId } from "../lib/helpGuides";
import type { HelpOutletContext } from "../lib/helpContext";
import { canManageMembers, canManageSources } from "../lib/role";
import { CURRENT_WHATS_NEW, getSeenWhatsNewReleaseId } from "../lib/whatsNew";
import { MainMenu, NavigationBreadcrumb } from "./AppNavigation";
import { useCurrentCart } from "./AddToCartButton";
import AppFooter from "./AppFooter";
import ArticleSearch from "./ArticleSearch";
import HelpLauncher from "./HelpDialog";
import WhatsNewLauncher from "./WhatsNewDialog";

// Kopfzeile Beta 30 (#630): Logo (= Dashboard) · Suche · Konto · Bestellungen ·
// Warenkorb, darunter das flache Hauptmenü. Hilfe und Neuerungen stehen im
// Konto-Menü statt als eigene Knöpfe.

const ORDER_PATHS = ["/bestellungen", "/preisanfrage", "/angebote", "/auftragsbestaetigungen", "/orders"];

/**
 * Die Kopfzeile bleibt ab Tablet-Breite beim Scrollen stehen. Ihre Höhe steht
 * als `--app-header-h` bereit, damit Sprungziele und angeheftete Bereiche
 * darunter statt dahinter landen. Am Handy scrollt sie mit (0 px).
 */
function usePinnedHeaderHeight(header: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const element = header.current;
    if (!element) return;
    const root = document.documentElement;
    const update = () => {
      const pinned = getComputedStyle(element).position === "sticky";
      root.style.setProperty("--app-header-h", `${pinned ? element.offsetHeight : 0}px`);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--app-header-h");
    };
  }, [header]);
}

export default function AppShell() {
  const { user, logout, logoutPending } = useSession();
  const demo = isDemoMode();
  const location = useLocation();
  const navigate = useNavigate();
  const helpReturnFocusRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  usePinnedHeaderHeight(headerRef);
  const helpDeepLinkOpen = new URLSearchParams(location.search).has(
    HELP_QUERY_PARAM,
  );
  const [contextualHelpGuideId, setContextualHelpGuideId] =
    useState<HelpGuideId | null>(null);
  const [whatsNewDialogOpen, setWhatsNewDialogOpen] = useState(false);
  const [whatsNewSignal, setWhatsNewSignal] = useState(0);
  const openHelpGuide = useCallback(
    (guideId: HelpGuideId, returnFocusTarget: HTMLElement | null) => {
      helpReturnFocusRef.current = returnFocusTarget;
      const params = new URLSearchParams(location.search);
      params.set(HELP_QUERY_PARAM, guideId);
      const search = params.toString();
      navigate({
        pathname: location.pathname,
        search: search ? `?${search}` : "",
        hash: location.hash,
      });
    },
    [location.hash, location.pathname, location.search, navigate],
  );
  const helpOutletContext = useMemo<HelpOutletContext>(
    () => ({ setContextualHelpGuideId, openHelpGuide }),
    [openHelpGuide, setContextualHelpGuideId],
  );
  const admin = canManageMembers(user);
  const openRouteHelp = () => {
    const guide = getHelpGuideForPath(location.pathname, location.search);
    openHelpGuide(canOpenHelpGuide(guide, admin) ? guide.id : getAvailableHelpGuides(admin)[0].id, null);
  };

  const canReviewPrices = user?.active_membership.can_review_prices === true;
  const openInvoices = useOpenInvoiceCount(canReviewPrices);
  const pendingImportsQuery = useQuery({
    queryKey: ["imports", "jobs", "cockpit"],
    queryFn: () => listImportJobs(),
    enabled: canReviewPrices && !demo,
    retry: false,
    staleTime: 60_000,
  });
  const openReviews = openInvoices.count;
  const pendingImports = (pendingImportsQuery.data ?? []).filter((job) => job.status === "review").length;
  const cartQuery = useCurrentCart();
  const cartLines = cartQuery.data?.lines.length ?? 0;
  const inOrders = ORDER_PATHS.some((path) => location.pathname === path || (location.pathname.startsWith(`${path}/`) && !location.pathname.startsWith("/angebote/vergleichen") && !location.pathname.startsWith("/angebote/auswahl")));
  const inCart = location.pathname.startsWith("/warenkorb");

  return (
    <div className="app-readable-contrast flex min-h-full flex-col">
      {demo && <DemoBanner />}
      <header ref={headerRef} className="relative z-40 sm:sticky sm:top-0">
        <div className="bg-ink text-white">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 sm:flex-nowrap sm:px-6">
            <Link to="/" className="flex-none text-lg font-bold tracking-tight text-white" title="Zum Dashboard">
              {/* Marken-Wortmarke: „Cost" trägt das Brand-Grün. Siehe docs/design_system.md. */}
              Truu<span className="text-on-ink">Cost</span>
            </Link>
            {user && (
              <div className="order-last w-full min-w-0 sm:order-none sm:mx-2 sm:flex-1">
                <ArticleSearch
                  prominent
                  belegeSearch={canReviewPrices}
                  placeholder={canReviewPrices ? "Artikel, Artikelnummer oder Beleg suchen …" : "Artikel oder Artikelnummer suchen …"}
                  ariaLabel={canReviewPrices ? "Artikel oder Beleg suchen" : "Artikel suchen"}
                />
              </div>
            )}
            <div className="ml-auto flex flex-none items-center gap-1">
              {user && (
                <KontoMenu
                  user={user}
                  demo={demo}
                  pendingImports={pendingImports}
                  whatsNewUnread={!demo && getSeenWhatsNewReleaseId() !== CURRENT_WHATS_NEW.id}
                  onOpenHelp={openRouteHelp}
                  onOpenWhatsNew={() => setWhatsNewSignal((value) => value + 1)}
                  logout={() => {
                    void logout();
                  }}
                  logoutPending={logoutPending}
                />
              )}
              {user && !demo && (
                <Link to="/bestellungen" aria-current={inOrders ? "page" : undefined}
                  aria-label="Bestellungen"
                  className={`flex h-10 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold hover:bg-white/10 ${inOrders ? "bg-white/10" : ""}`}>
                  <BoxIcon />
                  <span className="hidden md:inline">Bestellungen</span>
                </Link>
              )}
              {user && !demo && (
                <Link to="/warenkorb" aria-current={inCart ? "page" : undefined}
                  aria-label={cartLines ? `Warenkorb, ${cartLines} Positionen` : "Warenkorb"}
                  className={`relative flex h-10 items-center gap-1.5 rounded-md px-2.5 text-sm font-semibold hover:bg-white/10 ${inCart ? "bg-white/10" : ""}`}>
                  <CartIcon />
                  {cartLines ? (
                    <span className="absolute left-6 top-0.5 rounded-full bg-on-ink px-1.5 text-[11px] font-semibold tabular-nums text-ink">{cartLines}</span>
                  ) : null}
                  <span className="hidden md:inline">Warenkorb</span>
                </Link>
              )}
            </div>
          </div>
        </div>
        {user && <MainMenu counts={openReviews ? { Rechnungsprüfung: openReviews } : {}} />}
        {user && (
          <HelpLauncher
            hideTrigger
            admin={admin}
            contextualGuideId={contextualHelpGuideId}
            disabled={whatsNewDialogOpen}
            returnFocusRef={helpReturnFocusRef}
          />
        )}
        {user && !demo && (
          <WhatsNewLauncher
            hideTrigger
            openSignal={whatsNewSignal}
            blocked={helpDeepLinkOpen}
            onOpenChange={setWhatsNewDialogOpen}
            suppressAutomaticOpen={helpDeepLinkOpen}
          />
        )}
      </header>
      {user && <NavigationBreadcrumb />}
      <div className="flex-1">
        <Outlet context={helpOutletContext} />
      </div>
      <AppFooter />
    </div>
  );
}

function DemoBanner() {
  return (
    <div className="bg-emerald-600 text-white">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-2 gap-y-1 px-4 py-1.5 text-center text-xs sm:text-sm">
        <span className="font-medium">Interaktive Demo</span>
        <span className="text-emerald-100">
          Alle Daten sind anonymisiert oder synthetisch und dienen nur zur
          Veranschaulichung.
        </span>
        <a
          href={DEMO_MARKETING_URL}
          className="font-medium underline decoration-emerald-200 underline-offset-2 hover:text-emerald-50"
        >
          Zur Website →
        </a>
      </div>
    </div>
  );
}

function KontoMenu({
  user,
  demo,
  pendingImports,
  whatsNewUnread,
  onOpenHelp,
  onOpenWhatsNew,
  logout,
  logoutPending,
}: {
  user: SessionUser;
  demo: boolean;
  pendingImports: number;
  whatsNewUnread: boolean;
  onOpenHelp: () => void;
  onOpenWhatsNew: () => void;
  logout: () => void;
  logoutPending: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  useEffect(() => setIsOpen(false), [pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setIsOpen(false);
    }
    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const name = displayUserName(user);
  const item = "flex w-full items-center justify-between gap-2 rounded px-3 py-2 text-left text-slate-700 hover:bg-slate-50";
  const close = () => setIsOpen(false);
  return (
    <div ref={menuRef} className="relative flex-none">
      <button
        type="button"
        onClick={() => setIsOpen((value) => !value)}
        className="flex h-10 items-center gap-2 rounded-md px-2 text-sm font-semibold hover:bg-white/10"
        aria-label="Konto"
        data-account-menu-button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        title={name}
      >
        <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-slate-700 text-[11px] font-semibold uppercase">
          {initials(name)}
          {whatsNewUnread || pendingImports > 0 ? (
            <span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-on-ink ring-2 ring-ink" />
          ) : null}
        </span>
        <span className="hidden md:inline">Konto</span>
        <ChevronIcon />
      </button>
      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-md border border-slate-200 bg-white text-sm text-slate-900 shadow-xl"
        >
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="truncate font-medium">{name}</div>
            <div className="mt-0.5 text-xs text-slate-500">
              {demo ? "Demo-Arbeitsbereich" : user.active_membership.tenant_name ?? user.customer_id}
            </div>
          </div>
          {demo ? null : (
            <div className="p-1.5">
              {user.active_membership.can_upload_documents !== false ? (
                <Link to="/imports" role="menuitem" onClick={close} className={item}>
                  Belege hochladen
                  {pendingImports > 0 ? (
                    <span className="rounded-full bg-slate-100 px-1.5 text-[11px] font-medium text-slate-700">{pendingImports} prüfen</span>
                  ) : null}
                </Link>
              ) : null}
              {canManageMembers(user) ? (
                <Link to="/members" role="menuitem" onClick={close} className={item}>Benutzerverwaltung</Link>
              ) : null}
              {canManageSources(user) || user.active_membership.can_review_prices ? (
                <Link to="/einstellungen" role="menuitem" onClick={close} className={item}>Einstellungen</Link>
              ) : null}
            </div>
          )}
          <div className="border-t border-slate-100 p-1.5">
            {demo ? null : (
              <button type="button" role="menuitem" className={item} onClick={() => { close(); onOpenWhatsNew(); }}>
                Neuerungen
                {whatsNewUnread ? <span className="rounded-full bg-emerald-100 px-1.5 text-[11px] text-emerald-800">neu</span> : null}
              </button>
            )}
            <button type="button" role="menuitem" className={item} onClick={() => { close(); onOpenHelp(); }}>Hilfe</button>
          </div>
          {demo ? null : (
            <div className="border-t border-slate-100 p-1.5">
              <button type="button" role="menuitem" onClick={logout} disabled={logoutPending}
                className={`${item} text-slate-600 disabled:opacity-50`}>
                {logoutPending ? "…" : "Abmelden"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function initials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  return (parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2)) || "?";
}

function ChevronIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
  );
}

function BoxIcon() {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M21 8 12 3 3 8v8l9 5 9-5V8Z" /><path d="m3 8 9 5 9-5M12 13v8" /></svg>
  );
}

function CartIcon() {
  return (
    <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2.5 3.5h2.6l2.4 11.2a1.6 1.6 0 0 0 1.6 1.3h8.6a1.6 1.6 0 0 0 1.6-1.2l1.7-7.3H6.1" /></svg>
  );
}

function displayUserName(user: SessionUser): string {
  const emailName = user.email?.split("@")[0];
  const raw = emailName ?? user.user_id;
  if (!raw) return "Konto";
  if (
    !emailName &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      raw,
    )
  ) {
    return "Konto";
  }
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
